import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Chatbot.module.css';
import { useStreamingChat } from '../hooks/useStreamingChat';
import { PaperclipIcon, SendIcon } from './Icons';
import { ReactComponent as MenuIcon } from '../assets/icons/compass.svg';
import ArrowSend from './ArrowSend';
import FilePreviewCard from './FilePreviewCard';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiConfig';
import { sendChatMessageViaLambda, isLambdaAvailable } from '../utils/lambdaConfig';

const Chatbot = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, userId, getAccessTokenSilently } = useAuth0();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [fileContent, setFileContent] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState(null);
  const [isBackendLoading, setIsBackendLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);
  const initialQuerySent = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const realStreamingHook = useStreamingChat({
    onMessage: (message) => {
      setMessages(prev => [...prev, { role: 'assistant', content: message }]);
    },
    onComplete: () => {
      // Handle completion in useEffect
    },
    onError: (error) => {
      console.error('Streaming chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again later.' 
      }]);
    }
  });

  const { isLoading: isStreamingLoading, error: streamingError, streamingText, isComplete, sendStreamingMessage, reset } = realStreamingHook;

  // Combined loading state
  const isLoading = isStreamingLoading || isBackendLoading;

  const defaultQuickReplies = [
    { text: 'Homework help', payload: 'Help me with my homework', isNew: true },
    { text: 'Review my essay', payload: 'Review my essay' },
    { text: 'Suggest colleges', payload: 'Suggest colleges' },
    { text: 'Brainstorm essays', payload: 'Brainstorm essay topics' },
    { text: 'Improve my chances', payload: 'How can I improve my chances?' },
    { text: 'Paying for college', payload: 'How do I pay for college?' },
  ];

  // Auto-scroll to bottom when new messages arrive or streaming text updates
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Force scroll to bottom during streaming
  const forceScrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-scroll during streaming as text appears
  useEffect(() => {
    if (isStreamingLoading && streamingText) {
      // Use setTimeout to ensure DOM updates before scrolling
      setTimeout(() => {
        forceScrollToBottom();
      }, 10);
    }
  }, [streamingText, isStreamingLoading]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q && !initialQuerySent.current) {
      handleSend(q);
      initialQuerySent.current = true;
    }
  }, [location.search]);

  // Handle streaming completion
  useEffect(() => {
    if (isComplete && currentStreamingMessage && streamingText) {
      // Add the completed streaming message to messages
      const completedMessage = {
        ...currentStreamingMessage,
        content: streamingText,
        quickReplies: generateQuickReplies(streamingText) // Generate quick replies based on content
      };
      
      setMessages(prev => [...prev, completedMessage]);
      setCurrentStreamingMessage(null);
      
      // Reset streaming state
      reset();
      
      // Clear file state
      setFileContent(null);
      setSelectedFile(null);
    }
  }, [isComplete, streamingText, currentStreamingMessage, reset]);

  // Handle streaming errors
  useEffect(() => {
    if (streamingError && currentStreamingMessage) {
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I am having trouble connecting. Please try again later.',
        quickReplies: []
      };
      setMessages(prev => [...prev, errorMessage]);
      setCurrentStreamingMessage(null);
      reset();
      setFileContent(null);
      setSelectedFile(null);
    }
  }, [streamingError, currentStreamingMessage, reset]);

  const handleFileButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);

    // Support both .txt and .pdf files
    if (file.type === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFileContent(event.target.result);
      };
      reader.readAsText(file);
    } else if (file.type === 'application/pdf') {
      // For PDF files, we'll send the file directly to the backend
      // The backend will handle PDF parsing
      setFileContent('PDF_FILE_UPLOADED');
    } else {
      alert('Only .txt and .pdf files are supported at this time.');
      setSelectedFile(null);
    }
  };

  const handleFileOpen = (file) => {
    try {
      // If we have the original file object, use it directly
      if (file.fileObject) {
        const url = URL.createObjectURL(file.fileObject);
        
        // For PDFs, try to open in new tab for viewing
        if (file.type === 'application/pdf') {
          window.open(url, '_blank');
        } else {
          // For other files, trigger download
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
        // Clean up the URL after a short delay
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        // Show file info if we can't open it
        setFileContent(`File: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
      }
    } catch (error) {
      console.error('Error reading file:', error);
      setFileContent('Error reading file');
    }
  };

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    try {
      // Use OpenAI API for file uploads
      const formData = new FormData();
      formData.append('file', file);
      formData.append('message', input);
      formData.append('history', JSON.stringify(messages));

      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setMessages(prev => [...prev, { role: 'user', content: input }, { role: 'assistant', content: result.response }]);
      setInput('');
      setUploadedFile(null);
      setFileContent('');
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error uploading your file. Please try again.' 
      }]);
    } finally {
      setIsUploading(false);
    }
  };

  // Generate quick replies based on message content
  const generateQuickReplies = (content) => {
    const replies = [];
    
    if (content.toLowerCase().includes('homework') || content.toLowerCase().includes('assignment')) {
      replies.push({ text: 'More homework help', payload: 'I need more help with my homework' });
    }
    
    if (content.toLowerCase().includes('essay') || content.toLowerCase().includes('writing')) {
      replies.push({ text: 'Review another essay', payload: 'Can you review another essay for me?' });
      replies.push({ text: 'Essay tips', payload: 'Give me general essay writing tips' });
    }
    
    if (content.toLowerCase().includes('college') || content.toLowerCase().includes('university')) {
      replies.push({ text: 'More college suggestions', payload: 'Suggest more colleges for me' });
      replies.push({ text: 'Admission requirements', payload: 'What are the admission requirements?' });
    }
    
    // Add some general quick replies
    if (replies.length < 3) {
      replies.push({ text: 'Ask another question', payload: 'I have another question' });
    }
    
    return replies.slice(0, 3); // Limit to 3 quick replies
  };

  // Send message using Lambda API
  const sendDirectMessage = async (messageText) => {
    setIsBackendLoading(true);
    
    try {
      // Check if Lambda is available
      if (!isLambdaAvailable()) {
        throw new Error('Lambda API not configured');
      }

      // Get authentication token
      const token = await getAccessTokenSilently();
      if (!token) {
        throw new Error('Authentication required');
      }

      // Include file info in message if file is selected
      const fullMessage = selectedFile ? 
        `${messageText}\n\n[User uploaded file: ${selectedFile.name} - Note: File content cannot be processed in Lambda mode]` : 
        messageText;

      // Get chat history for context
      const chatHistory = messages.map(msg => ({ 
        role: msg.role, 
        content: msg.content 
      }));

      // Call Lambda API
      const response = await sendChatMessageViaLambda(fullMessage, chatHistory, token);
      
      const botMessage = { 
        role: 'assistant', 
        content: response.response,
        quickReplies: generateQuickReplies(response.response)
      };
      setMessages(prev => [...prev, botMessage]);

    } catch (error) {
      console.error("Failed to get response from Lambda API:", error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry I am having trouble connecting to the AI service. Please try again later.',
        quickReplies: []
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsBackendLoading(false);
      setFileContent(null);
      setSelectedFile(null);
    }
  };

  // Send message using direct OpenAI (replaces streaming)
  const sendStreamingMessageToOpenAI = async (messageText) => {
    await sendDirectMessage(messageText);
  };

  const handleSend = async (overrideMessage) => {
    // Handle message text and file attachment
    let messageText = overrideMessage !== undefined ? overrideMessage : input.trim();
    
    // If no message text but there's a file, create a default message
    if (!messageText && selectedFile) {
      messageText = "Please analyze the uploaded file.";
    }
    
    if (!messageText && !selectedFile) return;

    const userMessage = { 
      role: 'user', 
      content: messageText,
      attachment: selectedFile ? {
        name: selectedFile.name,
        type: selectedFile.type,
        size: selectedFile.size,
        fileObject: selectedFile // Store the actual file object for download
      } : null
    };
    setMessages(prev => [...prev, userMessage]);
    if (overrideMessage === undefined) setInput(''); // Only clear input if not a quick reply

    // Use direct OpenAI calls (files not supported yet)
    await sendDirectMessage(messageText);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSend();
  };

  const handleStopStreaming = () => {
    // Stop the current streaming
    reset();
    
    // Add a message indicating the stream was stopped
    if (currentStreamingMessage) {
      const stoppedMessage = {
        ...currentStreamingMessage,
        content: currentStreamingMessage.content + ' [Stopped]',
        quickReplies: []
      };
      setMessages(prev => [...prev, stoppedMessage]);
      setCurrentStreamingMessage(null);
    }
    
    // Clear file state
    setFileContent(null);
    setSelectedFile(null);
  };

  // Typing indicator component
  const TypingIndicator = () => (
    <div className={styles.typingIndicator}>
      <div className={styles.typingDot}></div>
      <div className={styles.typingDot}></div>
      <div className={styles.typingDot}></div>
    </div>
  );

  return (
    <div
      className={styles.chatbotContainer}
      style={{
        background: 'linear-gradient(135deg, #e0c3fc 0%, #8ec5fc 100%)',
      }}
    >
      <div className={styles.chatWindow}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate('/')}>
            ← Back
          </button>
          <div className={styles.avatar}>
            <img 
              src="/icons8-robot-48%20(1).png" 
              alt="AI Robot" 
              style={{ width: '32px', height: '32px' }}
            />
          </div>
          <div className={styles.chatInfo}>
            <div className={styles.chatName}>Chat with Quila</div>
            <div className={styles.status}>
              {isLoading ? 
                (isStreamingLoading ? 'Streaming response...' : 'Processing...') : 
                'We typically reply in a few minutes.'
              }
            </div>
          </div>
          <MenuIcon className={styles.menuIcon} />
        </div>
        <div className={styles.messages} ref={messagesContainerRef}>
          {messages.map((msg, index) => (
            <div key={index} className={msg.role === 'user' ? styles.userMessageContainer : styles.botMessageContainer}>
              {msg.role === 'assistant' && <div className={styles.avatar}>
                <img 
                  src="/icons8-robot-48%20(1).png" 
                  alt="AI Robot" 
                  style={{ width: '32px', height: '32px' }}
                />
              </div>}
              
              {/* Render file preview card above user message */}
              {msg.role === 'user' && msg.attachment && (
                <div className={styles.fileAttachmentCard}>
                  <FilePreviewCard 
                    file={msg.attachment} 
                    onOpen={handleFileOpen}
                  />
                </div>
              )}
              
              <div className={msg.role === 'user' ? styles.userMessage : styles.botMessage}>
                <div className={styles.messageContent}>
                  {msg.content && <p>{msg.content}</p>}
                </div>
                {msg.role === 'assistant' && msg.quickReplies && msg.quickReplies.length > 0 && (
                  <div className={styles.quickRepliesBar}>
                    {msg.quickReplies.map((reply, i) => (
                      <button key={i} className={styles.quickReplyPill} onClick={() => handleSend(reply.payload)}>
                        {reply.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {/* Show streaming message */}
          {isStreamingLoading && currentStreamingMessage && (
            <div className={styles.botMessageContainer}>
              <div className={styles.avatar}>
                <img 
                  src="/icons8-robot-48%20(1).png" 
                  alt="AI Robot" 
                  style={{ width: '32px', height: '32px' }}
                />
              </div>
              <div className={styles.botMessage}>
                <div className={styles.messageContent}>
                  <p>
                    {streamingText || ''}
                    {isStreamingLoading && <span className={styles.streamingCursor}>|</span>}
                  </p>
                </div>
                {isStreamingLoading && (
                  <button 
                    className={styles.stopButton}
                    onClick={handleStopStreaming}
                  >
                    Stop
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Show typing indicator when streaming starts but no text yet */}
          {isStreamingLoading && currentStreamingMessage && !streamingText && (
            <div className={styles.botMessageContainer}>
              <div className={styles.avatar}>
                <img 
                  src="/icons8-robot-48%20(1).png" 
                  alt="AI Robot" 
                  style={{ width: '32px', height: '32px' }}
                />
              </div>
              <div className={styles.botMessage}>
                <div className={styles.messageContent}>
                  <TypingIndicator />
                </div>
                <button 
                  className={styles.stopButton}
                  onClick={handleStopStreaming}
                >
                  Stop
                </button>
              </div>
            </div>
          )}
          
          {/* Show backend loading message */}
          {isBackendLoading && (
            <div className={styles.botMessageContainer}>
              <div className={styles.avatar}>
                <img 
                  src="/icons8-robot-48%20(1).png" 
                  alt="AI Robot" 
                  style={{ width: '32px', height: '32px' }}
                />
              </div>
              <div className={styles.botMessage}>
                <div className={styles.messageContent}>
                  <p>Processing your file...</p>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        <div className={styles.inputSection}>
          {messages.length === 0 && (
            <div className={styles.quickRepliesBar}>
              {defaultQuickReplies.map((reply, i) => (
                <button key={i} className={styles.quickReplyPill} onClick={() => handleSend(reply.payload)}>
                  {reply.text}
                  {reply.isNew && <span className={styles.newBadge}>New</span>}
                </button>
              ))}
            </div>
          )}
          <form className={styles.inputBar} onSubmit={handleSubmit} autoComplete="off">
            <input
              type="text"
              className={styles.inputField}
              placeholder={isStreamingLoading ? "AI is responding..." : "Ask me a question"}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={isLoading}
            />
            <button 
              type="submit" 
              className={styles.sendButton} 
              disabled={isLoading || (!input.trim() && !fileContent)}
              title={isStreamingLoading ? "Please wait for AI to finish responding" : "Send message"}
            >
              <ArrowSend />
            </button>
          </form>
          <div className={styles.fileUploadSection}>
            <label htmlFor="chatbot-file-upload" className={styles.fileUploadLabel}>
              <input
                id="chatbot-file-upload"
                type="file"
                accept=".txt,.pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={isLoading}
              />
              <span className={`${styles.fileUploadButton} ${isLoading ? styles.disabled : ''}`}>
                Upload TXT or PDF
              </span>
            </label>
            {selectedFile && (
              <div className={styles.filePreview}>
                <div className={styles.filePreviewContent}>
                  <span className={styles.fileIcon}>
                    {selectedFile.type === 'application/pdf' ? '📄' : '📝'}
                  </span>
                  <div className={styles.fileInfo}>
                    <span className={styles.fileName}>{selectedFile.name}</span>
                    <span className={styles.fileSize}>
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.removeFileButton}
                    onClick={() => {
                      setSelectedFile(null);
                      setFileContent(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    title="Remove file"
                    disabled={isLoading}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot; 